import { useSortable } from "@dnd-kit/sortable";
import React from "react";

import { CSS } from "@dnd-kit/utilities";
import {
  CheckIcon,
  CrossIcon,
  DeleteIcon,
  DragAndOrderIcon,
  EditIcon,
  PreviewIcon,
} from "../../../assets/icons";
import constant from "../../../utils/constant";
import CannedTestSectionGroupQuestion from "../../../model/cannedTests/cannedTestSectionGroupQuestion";

interface ISortableItemProps {
  question: CannedTestSectionGroupQuestion;
  index: number;
  isEdit: boolean;
  isEditIndex: number;
  groupIndex: number;
  onEditClick: (question: any, index: number) => void;
  onQuestionChange: (e: any, index: number, groupIndex: number) => void;
  onDeleteGroupQuestionClick: (groupIndex: number, index: number) => void;
  editQuestionHandler: (questionId: number) => void;
  onQuestionSubmit: () => void;
  onCancelClick: (index: number) => void;
  openModal: (question: CannedTestSectionGroupQuestion) => void;
}

const SectionGroupCardSortableItem: React.FC<ISortableItemProps> = ({
  question,
  index,
  isEdit,
  isEditIndex,
  groupIndex,
  onEditClick,
  onQuestionChange,
  onDeleteGroupQuestionClick,
  editQuestionHandler,
  onQuestionSubmit,
  onCancelClick,
  openModal,
}: ISortableItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: question.questionId.toString() });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Separate drag handle styles
  const dragHandleStyle = {
    cursor: isDragging ? "grabbing" : "grab",
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={index % 2 === 0 ? "bg-white" : "bg-gray-100"}
      key={index}
    >
      <td className="px-3 py-2">
        <div
          {...attributes}
          {...listeners}
          style={dragHandleStyle}
          className="inline-flex items-center justify-center"
        >
          <DragAndOrderIcon className="w-6 h-6" />
        </div>
      </td>
      <td className="px-3 py-2 whitespace-nowrap text-sm text-left font-normal text-gray-500">
        <p>{question.sort}</p>
      </td>
      <td className="px-3 py-2 whitespace-nowrap text-sm text-left font-normal text-gray-500">
        {isEditIndex !== index && (
          <a
            href={constant.MANAGEQUESTION.replace(
              ":id",
              question.questionId.toString()
            )}
            className="cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
            onClick={() => {
              editQuestionHandler(question.questionId);
            }}
          >
            {question.questionId}
          </a>
        )}
        {isEdit && isEditIndex === index && (
          <input
            id={`questionId_${index}`}
            name={`questionId_${index}`}
            defaultValue={question.questionId || ""}
            type="number"
            onBlur={(e) => onQuestionChange(e, index, groupIndex)}
            onWheel={(e) => e.currentTarget.blur()}
            className="block w-full max-w-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm"
          />
        )}
      </td>
      <td>
        <div className="flex items-center ">
          <a onClick={() => openModal(question)} className="text-gray-900">
            <PreviewIcon className="mr-3 h-5 w-5 text-xs font-light cursor-pointer text-gray-400 hover:text-secondary-color transaction ease-in-out duration-300" />
          </a>
        </div>
      </td>
      <td className="px-3 py-2 whitespace-nowrap text-sm text-left font-normal text-gray-700">
        <div className="flex gap-3 justify-center">
          {!isEdit && (
            <EditIcon
              title="Edit"
              onClick={() => onEditClick(question, index)}
              className="w-4 text-xs font-light cursor-pointer hover:text-secondary-color transaction ease-in-out duration-300"
            />
          )}
          {isEdit && isEditIndex === index && (
            <CheckIcon
              onClick={onQuestionSubmit}
              className="w-4 text-xs font-light cursor-pointer hover:text-secondary-color transaction ease-in-out duration-300"
            />
          )}
          {isEdit && isEditIndex === index && (
            <CrossIcon
              title="Cancel"
              onClick={() => onCancelClick(index)}
              className="w-4 text-xs font-light cursor-pointer hover:text-secondary-color transaction ease-in-out duration-300"
            />
          )}
          <DeleteIcon
            onClick={() => onDeleteGroupQuestionClick(groupIndex, index)}
            className="w-4 text-xs font-light cursor-pointer hover:text-red-500 transaction ease-in-out duration-300"
          />
        </div>
      </td>
    </tr>
  );
};

export default SectionGroupCardSortableItem;
